import { IResponseMessage } from 'src/app/core/models/api/sane-project/arpa-model';
import { toBooleanStrict, toISO, toNumber } from 'src/app/shared/utils';
import { ProjectViewModel } from '../models/project.viewmodel';
import { IProjectDraftDto } from './project-draft.dto';

interface ICalcDraftPerProduct {
  form: Record<string, any>;
  bonus?: number | null;
  chargeable?: boolean | null;
  productName?: string | null;
  trace?: string | null;
  maximumCapital?: number | null;
  hasTelesubscription?: boolean | null;
  telesubscriptionDirect?: boolean | null;
  telesubscriptionSingleQuestion?: boolean | null;
  summaryCapitals?: string | null;
  limitsExceeded?: boolean | null;
  messages?: IResponseMessage[] | null;
}

type CalcDraftCategory = Record<string, ICalcDraftPerProduct>;

export class ProjectDraftMapper {
  private static getFlex(vm: ProjectViewModel, ref: string): any {
    if (!ref) { return null; }
    if (ref.startsWith('edit.')) {
      return vm.form.get(ref)?.value ?? null;
    }
    return vm.edit.getControl(ref)?.value ?? null;
  }

  private static serializeCategory(category: Record<string, any>): CalcDraftCategory {
    const out: CalcDraftCategory = {};
    Object.entries(category).forEach(([key, productVm]: [string, any]) => {
      out[key] = {
        form: productVm.form?.getRawValue?.() ?? {},
        bonus: productVm.bonus ?? null,
        chargeable: productVm.chargeable ?? null,
        productName: productVm.productName ?? null,
        trace: productVm.trace ?? null,
        maximumCapital: productVm.maximumCapital ?? null,
        hasTelesubscription: productVm.hasTelesubscription ?? null,
        telesubscriptionDirect: productVm.telesubscriptionDirect ?? null,
        telesubscriptionSingleQuestion: productVm.telesubscriptionSingleQuestion ?? null,
        summaryCapitals: productVm.summaryCapitals ?? null,
        limitsExceeded: productVm.limitsExceeded ?? null,
        messages: Array.isArray(productVm.messages) ? productVm.messages : null
      };
    });
    return out;
  }

  private static applyCategoryDraft(draft: any, target: Record<string, any>): void {
    if (!draft || !target) { return; }
    Object.keys(target).forEach(key => {
      const productVm = target[key];
      const node = draft[key];
      if (node && typeof node === 'object') {
        const formPatch = node.form && typeof node.form === 'object' ? node.form : node;
        if (productVm?.form?.patchValue) {
          productVm.form.patchValue(formPatch, { emitEvent: false });
        }
        if ('bonus' in node) { productVm.bonus = node.bonus ?? 0; }
        if ('chargeable' in node) { productVm.chargeable = !!node.chargeable; }
        if ('productName' in node) { productVm.productName = node.productName ?? ''; }
        if ('trace' in node) { productVm.trace = node.trace ?? ''; }
        if ('maximumCapital' in node) { productVm.maximumCapital = node.maximumCapital ?? 0; }
        if ('hasTelesubscription' in node) { productVm.hasTelesubscription = !!node.hasTelesubscription; }
        if ('telesubscriptionDirect ' in node) { productVm.telesubscriptionDirect = !!node.telesubscriptionDirect; }
        if ('telesubscriptionSingleQuestion' in node) { productVm.telesubscriptionSingleQuestion = !!node.telesubscriptionSingleQuestion; }
        if ('summaryCapitals' in node) { productVm.summaryCapitals = node.summaryCapitals ?? ''; }
        if ('limitsExceeded' in node) { productVm.limitsExceeded = !!node.limitsExceeded; }
        if ('messages' in node && Array.isArray(node.messages)) {
          productVm.messages = node.messages as IResponseMessage[];
        }
      }
    });
  }

  static toDraft(vm: ProjectViewModel): IProjectDraftDto {
    const edit = vm.edit;
    const getPath = (path: string) => vm.form.get(path)?.value ?? null;

    return {
      projectId: toNumber(getPath('edit.projectId')),
      projectName: String(getPath('edit.projectName') ?? ''),
      industry: {
        life: toBooleanStrict(this.getFlex(vm, edit.cIndustryLife)),
        accident: toBooleanStrict(this.getFlex(vm, edit.cIndustryAccident)),
        temporaryIncapacity: toBooleanStrict(this.getFlex(vm, edit.cIndustryTemporaryIncapacity)),
        dependency: toBooleanStrict(this.getFlex(vm, edit.cIndustryDependency)),
        wholeLife: toBooleanStrict(this.getFlex(vm, 'edit.projectIndustryWholeLife')),
        death: toBooleanStrict(this.getFlex(vm, edit.cIndustryDeath))
      },
      client: {
        id: toNumber(getPath('edit.projectClientId')),
        name: String(getPath('edit.projectClientName') ?? ''),
        professionCode: toNumber(getPath('edit.projectClientProfessionCode')),
        birthDate: toISO(getPath('edit.projectClientBirthDate')),
        clientSex: toNumber(getPath('edit.projectClientSex'))
      },
      policy: {
        paymentType: toNumber(getPath('edit.projectPaymentType')),
        effectiveDate: toISO(getPath('edit.projectEffectiveDate')),
        regime: String(getPath('edit.projectActivity') ?? ''),
        sport: toNumber(getPath('edit.projectSport')),
        risks: toNumber(getPath('edit.projectLifeEspecialRisks')),
        motorcycle: toNumber(getPath('edit.projectLifeMotorbike')),
        motor: toBooleanStrict(this.getFlex(vm, 'edit.projectLifeDrivingForce')),
        scaffolding: toBooleanStrict(this.getFlex(vm, 'edit.projectLifeScaffoldRoof')),
        lifeLevel: toNumber(getPath('edit.projectLivingStandard')),
        married: toBooleanStrict(this.getFlex(vm, 'edit.projectMarried')),
        children: toNumber(getPath('edit.projectMinorChildren')),
        mutual: toNumber(getPath('edit.projectMutualIncome')),
        retaQuote: toNumber(getPath('edit.projectContributionRETA')),
        generalRegimeQuote: toNumber(getPath('edit.projectContributionGeneralReg'))
      },
      calculatorLife: this.serializeCategory(vm.calculatorLife),
      calculatorAccidents: this.serializeCategory(vm.calculatorAccidents),
      calculatorIT: this.serializeCategory(vm.calculatorIT),
      calculatorDependence: this.serializeCategory(vm.calculatorDependence),
      calculatorLifeWhole: this.serializeCategory(vm.calculatorLifeWhole),
      calculatorDeaths: this.serializeCategory(vm.calculatorDeaths)
    };
  }

  static fromDraft(d: IProjectDraftDto, createVm: () => ProjectViewModel): ProjectViewModel {
    const vm = createVm();
    const e = vm.edit;
    const set = (ctrl: string, val: any) => e.getControl(ctrl)?.setValue(val);

    vm.form.get('edit.projectId')?.setValue(d.projectId ?? null);
    vm.form.get('edit.projectName')?.setValue(d.projectName ?? '');

    set(e.cIndustryLife, !!d.industry.life);
    set(e.cIndustryAccident, !!d.industry.accident);
    set(e.cIndustryTemporaryIncapacity, !!d.industry.temporaryIncapacity);
    set(e.cIndustryDependency, !!d.industry.dependency);
    set(e.cIndustryWholeLife, !!d.industry.wholeLife);
    set(e.cIndustryDeath, !!d.industry.death);

    set(e.cClientId, d.client.id ?? null);
    set(e.cClientName, d.client.name ?? '');
    set(e.cClientProfessioncode, d.client.professionCode ?? null);
    set(e.cClientBirthdate, d.client.birthDate ? new Date(d.client.birthDate) : null);
    set(e.cClientSex, d.client.clientSex ?? null);

    set(e.cPaymentType, d.policy.paymentType ?? null);
    set(e.cEffectiveDate, d.policy.effectiveDate ? new Date(d.policy.effectiveDate) : null);
    set(e.cActivity, d.policy.regime ?? '');
    set(e.cSport, d.policy.sport ?? null);
    set(e.cLifeSpecialRisks, d.policy.risks ?? null);
    set(e.cLifeMotorbike, d.policy.motorcycle ?? null);
    set(e.cLifeDrivingForce, !!d.policy.motor);
    set(e.cLifeScaffoldRoof, !!d.policy.scaffolding);
    set(e.cLivingStandard, d.policy.lifeLevel ?? null);
    set(e.cMarried, !!d.policy.married);
    set(e.cMinorChildren, d.policy.children ?? null);
    set(e.cMutualIncome, d.policy.mutual ?? null);
    set(e.cContributionReta, d.policy.retaQuote ?? null);
    set(e.cContributionGeneralRegime, d.policy.generalRegimeQuote ?? null);

    if (d.calculatorLife) { this.applyCategoryDraft(d.calculatorLife, vm.calculatorLife); }
    if (d.calculatorAccidents) { this.applyCategoryDraft(d.calculatorAccidents, vm.calculatorAccidents); }
    if (d.calculatorIT) { this.applyCategoryDraft(d.calculatorIT, vm.calculatorIT); }
    if (d.calculatorDependence) { this.applyCategoryDraft(d.calculatorDependence, vm.calculatorDependence); }
    if (d.calculatorLifeWhole) { this.applyCategoryDraft(d.calculatorLifeWhole, vm.calculatorLifeWhole); }
    if (d.calculatorDeaths) { this.applyCategoryDraft(d.calculatorDeaths, vm.calculatorDeaths); }

    vm.form.updateValueAndValidity({ emitEvent: false });
    return vm;
  }
}
